/**
 * Per-user, per-table column preferences — which columns are shown and how wide
 * they are — persisted in localStorage.
 *
 * Framework-free on purpose: every rule that decides what a stored blob MEANS
 * lives here as a pure function, so it is unit-testable (the vitest config only
 * matches `src/**\/*.test.ts`, never `.tsx`), and `useTableColumns` on top is a
 * thin React wrapper holding state.
 *
 * ## Where a layout actually lives
 *
 * **The BACKEND is the source of truth**, as one `UserPreference` row per user
 * per table under the key `table.columns.<tableId>` (see `preferenceKeyFor`).
 * A column layout is a preference that belongs to a person, so it follows them
 * to another machine and another browser rather than being stranded in whichever
 * one they happened to set it in.
 *
 * localStorage is kept as a **cache in front of that**, not as the store. It
 * exists for one frame: on a cold load the server map has not arrived yet, and
 * without a local copy every table would render at its defaults and then snap to
 * the saved layout — a visible flash of the columns the user had hidden. The
 * cache is written on every save and refreshed from the server response, so it
 * is normally a frame ahead rather than a second source of truth. When the two
 * disagree, the server wins.
 *
 * ## Keying
 *
 * Server: `table.columns.<tableId>`, owned by the authenticated user — the
 * backend never takes an owner from the request.
 *
 * Cache: `cgt.table.prefs.<scope>.<tableId>`, where `scope` is the user's id, so
 * two people sharing a browser profile do not inherit each other's layouts.
 *
 * Either way `tableId` is a released identifier: changing one silently abandons
 * everyone's saved layout for that table. Pick it once and leave it alone.
 *
 * ## Trust
 *
 * Both sources hold client-authored JSON — the cache is hand-editable text in
 * the user's own browser, and the server stores the value opaquely, exactly as
 * some earlier version of this client sent it. So neither is trusted:
 * `coerceTablePrefs` validates the shape field by field and discards anything
 * malformed rather than casting. A corrupted value must not be able to blank a
 * table.
 */

/** One column a table offers, as declared by the component that renders it. */
export interface TableColumnDef {
  /**
   * Stable identifier, persisted. Must not change once shipped — see the keying
   * note above; a rename reads as a different column and loses the saved state.
   */
  id: string
  /** Human-readable name, shown in the column picker. */
  header: string
  /**
   * Columns that must always render: the one that identifies the row, and the
   * actions cell. Without at least one such column a user can hide their way
   * into a table they can no longer act on or read.
   */
  locked?: boolean
  /** Shown only once the user opts in. For secondary columns. */
  defaultHidden?: boolean
  /** Starting width in px. Absent means "whatever the browser lays out". */
  defaultWidth?: number
  /**
   * Park this column against the right edge while the rest of the row scrolls
   * underneath it. For the actions cell on tables wide enough to scroll
   * sideways, where it is otherwise the first thing pushed off-screen.
   *
   * Declaration-level, deliberately: this is a property of the column, not a
   * preference, so it is never persisted and a user cannot end up with a table
   * whose actions they have unpinned into the far right of a scroll.
   *
   * Not to be confused with `ResolvedColumn.pinned`, which is about WIDTH — it
   * means the user dragged this column, so hold that width exactly.
   */
  sticky?: 'right'
  /**
   * The header offers ascending/descending sorting (z8nrz7e4h6). Opt-in per
   * column: the table must also supply how to sort it — an accessor for a
   * client-side table, an allowed `sort` key for a server-paged one. Not for
   * actions, checkboxes or columns that show only a control.
   */
  sortable?: boolean
}

/** The persisted shape. `v` lets a later change migrate rather than guess. */
export interface TableColumnPrefs {
  v: 1
  /** Column ids the user has hidden. Locked ids are never stored here. */
  hidden: string[]
  /** Column id → width in px. */
  widths: Record<string, number>
  /** The column the table is sorted by, if the user chose one (z8nrz7e4h6). */
  sort?: { id: string; dir: 'asc' | 'desc' }
}

/** A column paired with the user's resolved preferences for it. */
export interface ResolvedColumn {
  def: TableColumnDef
  visible: boolean
  /** Effective width in px, or undefined to let the browser decide. */
  width?: number
  /**
   * Whether that width is one the USER dragged, as opposed to the column's
   * declared default.
   *
   * The two must not render the same way. A dragged width is an instruction —
   * pin the column there and clip what does not fit. A default is a preference,
   * and pinning it means the table can never compress: nine defaults totalling
   * 1340px cannot shrink for a narrower screen, so the browser pushes the last
   * column (the actions cell, every time) off the right-hand edge, where the
   * page's own horizontal scroll is the only way to reach it.
   */
  pinned: boolean
}

const KEY_PREFIX = 'cgt.table.prefs'

/**
 * Server-side key namespace for table layouts.
 *
 * Also what "reset all layouts" passes as a prefix, so it clears only table
 * layouts and leaves any other preference a later feature stores alone.
 */
export const PREFERENCE_KEY_PREFIX = 'table.columns.'

/**
 * Width bounds for a drag. The floor keeps a column wide enough to grab again
 * (a 0px column cannot be resized back), and the ceiling stops one drag from
 * pushing every other column out of view.
 */
export const MIN_COLUMN_WIDTH = 56
export const MAX_COLUMN_WIDTH = 800

export const EMPTY_PREFS: TableColumnPrefs = { v: 1, hidden: [], widths: {} }

/** Storage key for one table's prefs. `scope` is the user id, or 'anon'. */
export function tablePrefsKey(scope: string, tableId: string): string {
  return `${KEY_PREFIX}.${scope || 'anon'}.${tableId}`
}

export function clampColumnWidth(px: number): number {
  // Only NaN needs the early return: it has no ordering, so Math.min/max would
  // propagate it straight through to `width: NaNpx`. ±Infinity does order, and
  // falls out of the clamp at the ceiling and the floor respectively.
  if (Number.isNaN(px)) return MIN_COLUMN_WIDTH
  return Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(px)))
}

/** Server preference key for one table's layout. */
export function preferenceKeyFor(tableId: string): string {
  return `${PREFERENCE_KEY_PREFIX}${tableId}`
}

/**
 * Validate an already-parsed value into usable prefs, discarding anything
 * malformed.
 *
 * The single validation path for BOTH sources — the localStorage cache and the
 * server's stored JSON. Neither is trusted (see the Trust note above), and
 * having one function means a shape that is rejected from one is rejected from
 * the other.
 */
export function coerceTablePrefs(parsed: unknown): TableColumnPrefs {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return EMPTY_PREFS
  const obj = parsed as Record<string, unknown>

  const hidden = Array.isArray(obj.hidden)
    ? [...new Set(obj.hidden.filter((id): id is string => typeof id === 'string' && id !== ''))]
    : []

  const widths: Record<string, number> = {}
  if (obj.widths && typeof obj.widths === 'object' && !Array.isArray(obj.widths)) {
    for (const [id, px] of Object.entries(obj.widths as Record<string, unknown>)) {
      // A non-numeric or non-finite width would reach the DOM as `width: NaNpx`
      // and collapse the column, so drop it rather than clamp it.
      if (typeof px === 'number' && Number.isFinite(px)) widths[id] = clampColumnWidth(px)
    }
  }
  const sort = coerceSort(obj.sort)
  return sort ? { v: 1, hidden, widths, sort } : { v: 1, hidden, widths }
}

function coerceSort(raw: unknown): TableColumnPrefs['sort'] {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const { id, dir } = raw as Record<string, unknown>
  if (typeof id !== 'string' || id === '' || (dir !== 'asc' && dir !== 'desc')) return undefined
  return { id, dir }
}

/**
 * Parse a stored cache blob into valid prefs.
 *
 * Exported separately from `readTablePrefs` so the JSON handling is testable
 * without touching a real `localStorage`.
 */
export function parseTablePrefs(raw: string | null): TableColumnPrefs {
  if (!raw) return EMPTY_PREFS
  try {
    return coerceTablePrefs(JSON.parse(raw))
  } catch {
    return EMPTY_PREFS
  }
}

/** Read one table's cached prefs. Never throws; unreadable storage means defaults. */
export function readTablePrefs(scope: string, tableId: string): TableColumnPrefs {
  try {
    return parseTablePrefs(localStorage.getItem(tablePrefsKey(scope, tableId)))
  } catch {
    return EMPTY_PREFS
  }
}

/**
 * Persist one table's prefs. Never throws.
 *
 * Prefs equal to the defaults are REMOVED rather than written, so "reset" leaves
 * no key behind and a table the user never customized costs nothing.
 */
export function writeTablePrefs(scope: string, tableId: string, prefs: TableColumnPrefs): void {
  const key = tablePrefsKey(scope, tableId)
  try {
    if (!hasSavedPrefs(prefs)) {
      localStorage.removeItem(key)
      return
    }
    localStorage.setItem(key, JSON.stringify(prefs))
  } catch {
    // Quota or a storage-blocking browser. The layout stays correct for this
    // session and simply does not persist — not worth failing a render over.
  }
}

/** Forget one table's prefs. */
export function clearTablePrefs(scope: string, tableId: string): void {
  try {
    localStorage.removeItem(tablePrefsKey(scope, tableId))
  } catch {
    // See writeTablePrefs.
  }
}

/** Every table id this scope has saved prefs for. */
export function listCustomizedTableIds(scope: string): string[] {
  const prefix = `${KEY_PREFIX}.${scope || 'anon'}.`
  const ids: string[] = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(prefix)) ids.push(key.slice(prefix.length))
    }
  } catch {
    return []
  }
  return ids.sort()
}

/** Forget every table's prefs for one scope. Used by "reset all layouts". */
export function clearAllTablePrefs(scope: string): void {
  for (const tableId of listCustomizedTableIds(scope)) clearTablePrefs(scope, tableId)
}

/**
 * Apply prefs to a column set.
 *
 * A locked column is always visible however the stored blob was edited, and an
 * id in `hidden` that no longer exists in `defs` is ignored (a column removed in
 * a later release must not resurrect as a phantom entry).
 */
export function resolveTableColumns(
  defs: TableColumnDef[],
  prefs: TableColumnPrefs,
): ResolvedColumn[] {
  return defs.map(def => ({
    def,
    visible: isColumnVisible(def, prefs),
    width: prefs.widths[def.id] ?? def.defaultWidth,
    pinned: prefs.widths[def.id] !== undefined,
  }))
}

/**
 * Whether a column renders, given the prefs.
 *
 * `defaultHidden` and the stored `hidden` list are deliberately combined as
 * "the stored list is the whole truth once the user has touched this table":
 * a default-hidden column the user turned ON must not be re-hidden by its own
 * default, which is why the default is folded into the prefs at first use
 * (`seedDefaultHidden`) rather than consulted on every read.
 */
export function isColumnVisible(def: TableColumnDef, prefs: TableColumnPrefs): boolean {
  if (def.locked) return true
  return !prefs.hidden.includes(def.id)
}

/**
 * Fold `defaultHidden` into prefs the first time a table is seen.
 *
 * Returns the prefs unchanged once anything is stored for the table, so this can
 * never re-hide a column the user has since switched on.
 */
export function seedDefaultHidden(
  defs: TableColumnDef[],
  prefs: TableColumnPrefs,
  hasStoredPrefs: boolean,
): TableColumnPrefs {
  if (hasStoredPrefs) return prefs
  const defaults = defs.filter(d => d.defaultHidden && !d.locked).map(d => d.id)
  if (defaults.length === 0) return prefs
  return { ...prefs, hidden: [...new Set([...prefs.hidden, ...defaults])] }
}

/**
 * Show or hide one column.
 *
 * Refuses two things outright, because both produce a table the user cannot
 * recover from inside the picker: hiding a `locked` column, and hiding the last
 * visible one.
 */
export function setColumnVisible(
  defs: TableColumnDef[],
  prefs: TableColumnPrefs,
  id: string,
  visible: boolean,
): TableColumnPrefs {
  const def = defs.find(d => d.id === id)
  if (!def || def.locked) return prefs
  if (visible) {
    if (!prefs.hidden.includes(id)) return prefs
    return { ...prefs, hidden: prefs.hidden.filter(h => h !== id) }
  }
  if (prefs.hidden.includes(id)) return prefs
  const stillVisible = defs.filter(d => d.id !== id && isColumnVisible(d, prefs)).length
  if (stillVisible === 0) return prefs
  return { ...prefs, hidden: [...prefs.hidden, id] }
}

/** Set one column's width, clamped to the drag bounds. */
export function setColumnWidth(
  prefs: TableColumnPrefs,
  id: string,
  px: number,
): TableColumnPrefs {
  return { ...prefs, widths: { ...prefs.widths, [id]: clampColumnWidth(px) } }
}

/** Drop one column's stored width, returning it to its default. */
export function clearColumnWidth(prefs: TableColumnPrefs, id: string): TableColumnPrefs {
  if (!(id in prefs.widths)) return prefs
  const widths = { ...prefs.widths }
  delete widths[id]
  return { ...prefs, widths }
}

/**
 * True when the user has customized this table's LAYOUT — hidden or resized a
 * column. Deliberately not the sort: this also decides whether default-hidden
 * columns are still seeded, and choosing a sort must not suddenly reveal them.
 */
export function isCustomized(prefs: TableColumnPrefs): boolean {
  return prefs.hidden.length > 0 || Object.keys(prefs.widths).length > 0
}

/** True when anything is worth storing: a layout change or a chosen sort. */
export function hasSavedPrefs(prefs: TableColumnPrefs): boolean {
  return isCustomized(prefs) || !!prefs.sort
}

/** Set, or clear with null, the column the table is sorted by. */
export function setColumnSort(
  prefs: TableColumnPrefs,
  sort: TableColumnPrefs['sort'] | null,
): TableColumnPrefs {
  if (!sort) {
    if (!prefs.sort) return prefs
    const { sort: _dropped, ...rest } = prefs
    return rest
  }
  return { ...prefs, sort }
}
